import { FC, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiViewMorePopOver,
} from "../../components";
import { PAGE_IDS, viewMorePopOverGridSize } from "../../helpers/constants";
import {
  formatWithTimezone,
  getValueOrBlank,
  getValueOrDash,
} from "../../helpers/utils";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useSiteCore } from "../../hooks/useSiteCore";
import { ViewUserWrapper } from "./ViewSystemMessage.style";

import { useGetSystemMessageDetailsQuery } from "../../api/services/systemMessage/SystemMessageApi";
import MuiChip from "../../components/chip/MuiChip";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { createSystemMessageSiteCoreKeys } from "../createSystemMessage/siteCore/SiteCoreKeys";

export const pageId = PAGE_IDS.VIEW_SYSTEM_MESSAGE;

interface ViewSystemMessageProps {
  open: boolean;
  selectedId: string;
  onClose: () => void;
  handleEditMessage: (staffId: string) => void;
  userCountryDateFormatWithTime: string;
}

export const ViewSystemMessage: FC<ViewSystemMessageProps> = ({
  open,
  selectedId,
  onClose,
  handleEditMessage,
  userCountryDateFormatWithTime,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    data: messageData,
    isFetching: isSystemMessageDetailsLoading,
    isError: isSystemMessageDetailsError,
    refetchWithParams: refetchSystemMessageDetails,
  } = useGetSystemMessageDetailsQuery();

  useEffect(() => {
    if (selectedId) {
      refetchSystemMessageDetails({ id: selectedId });
    }
  }, [selectedId]);

  useEffect(() => {
    if (isSystemMessageDetailsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    }
  }, [isSystemMessageDetailsError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        createUser: {
          isVisible: isSystemMessageDetailsLoading,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    };
  }, [isSystemMessageDetailsLoading]);

  const getMessageLabel = (key: string) =>
    fetchLabel(createSystemMessageSiteCoreKeys.createMessageKey, key);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const selectedPreference = useMemo(() => {
    return [
      ...(messageData?.linkToMessage
        ? [
            {
              value: "linkToMessage",
              label: getMessageLabel(createSystemMessageSiteCoreKeys.linkAdded),
            },
          ]
        : []),
      ...(messageData?.isPersistent
        ? [
            {
              value: "isPersistent",
              label: getMessageLabel(
                createSystemMessageSiteCoreKeys.persistentMessage,
              ),
            },
          ]
        : []),
    ];
  }, [messageData]);
  return (
    <MuiModal
      onClose={onClose}
      title={`${fetchLabel(createSystemMessageSiteCoreKeys.headerKey, createSystemMessageSiteCoreKeys.viewSystemMessagesHeader)} - [${getValueOrBlank(messageData?.messageIdFormat)}]`}
      open={open}
      tertiaryButtonTitle={commonLabels.edit}
      onTertiaryButtonClick={() => handleEditMessage(selectedId)}
      onSecondaryButtonClick={onClose}
      secondaryButtonTitle={commonLabels.close}
      hasLargeContent
    >
      <ViewUserWrapper>
        <MuiGrid container className="view-message message-country">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.messageId)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.messageIdFormat)}
              isValueWithEllipsis
              className="message-id"
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.country)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.countryName)}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.language)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.languageName)}
              isValueWithEllipsis
            />
          </MuiGrid>
        </MuiGrid>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getMessageLabel(
              createSystemMessageSiteCoreKeys.messageDetailsSection,
            ),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiGrid container className="view-message">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getMessageLabel(createSystemMessageSiteCoreKeys.title)}
                replaceEmptyWithDash
                value={getValueOrBlank(messageData?.messageTitle)}
                isValueWithEllipsis
              />
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container className="view-message">
            <MuiGrid
              size={viewMorePopOverGridSize}
              className={`${gridSizeMediumClass} message-description`}
            >
              <MuiDoubleLabel
                label={getMessageLabel(createSystemMessageSiteCoreKeys.message)}
                replaceEmptyWithDash
                value={
                  messageData?.messageDescription ? (
                    <MuiViewMorePopOver
                      anchorText={commonLabels.viewMore}
                      message={getValueOrDash(
                        messageData?.messageDescription ?? "",
                      )}
                      placement="bottom-end"
                    />
                  ) : (
                    ""
                  )
                }
              />
            </MuiGrid>
          </MuiGrid>
          {(messageData?.linkToMessage || messageData?.isPersistent) && (
            <MuiBox className="select-preferences">
              <MuiGrid container className="view-message">
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getMessageLabel(
                      createSystemMessageSiteCoreKeys.selectedPreferences,
                    )}
                    value={""}
                  />
                  <MuiChip
                    data={selectedPreference}
                    isEditable={false}
                    showMoreARIALabel={commonARIALabel.showMoreDetails}
                    removeChipNameARIALabel={commonARIALabel.removeChipName}
                  />
                </MuiGrid>
              </MuiGrid>
              {messageData?.linkToMessage && (
                <MuiGrid container className="view-message">
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getMessageLabel(
                        createSystemMessageSiteCoreKeys.linkName,
                      )}
                      replaceEmptyWithDash
                      value={getValueOrBlank(messageData?.linkName)}
                      isValueWithEllipsis
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getMessageLabel(
                        createSystemMessageSiteCoreKeys.linkURL,
                      )}
                      replaceEmptyWithDash
                      value={getValueOrBlank(messageData?.linkUrl)}
                      isValueWithEllipsis
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getMessageLabel(
                        createSystemMessageSiteCoreKeys.linkPosition,
                      )}
                      replaceEmptyWithDash
                      value={getValueOrBlank(
                        fetchValueByKeyOfDropdown(
                          createSystemMessageSiteCoreKeys.createMessageKey,
                          createSystemMessageSiteCoreKeys.linkPositionList,
                          messageData?.linkPosition,
                        ),
                      )}
                      isValueWithEllipsis
                    />
                  </MuiGrid>
                </MuiGrid>
              )}
            </MuiBox>
          )}
        </AccordionHeader>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getMessageLabel(createSystemMessageSiteCoreKeys.durationSection),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiGrid container className="view-message message-country">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getMessageLabel(
                  createSystemMessageSiteCoreKeys.timeZone,
                )}
                replaceEmptyWithDash
                value={getValueOrBlank(messageData?.timezoneName)}
                isValueWithEllipsis
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getMessageLabel(
                  createSystemMessageSiteCoreKeys.fromDateTime,
                )}
                replaceEmptyWithDash
                value={formatWithTimezone(
                  messageData?.fromDate,
                  messageData?.timezone,
                  userCountryDateFormatWithTime,
                )}
                isValueWithEllipsis
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getMessageLabel(
                  createSystemMessageSiteCoreKeys.toDateTime,
                )}
                replaceEmptyWithDash
                value={formatWithTimezone(
                  messageData?.toDate,
                  messageData?.timezone,
                  userCountryDateFormatWithTime,
                )}
                isValueWithEllipsis
              />
            </MuiGrid>
          </MuiGrid>
        </AccordionHeader>
      </ViewUserWrapper>
    </MuiModal>
  );
};
